import { useEffect, useMemo, useState } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { Link, useNavigate } from 'react-router-dom';
import { PencilIcon, PlusIcon, SearchIcon, TrashIcon } from '@/components/catalog/icons';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import type { MarketingBlock } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * The Blocks tab — reusable headers, footers and sections over
 * `GET /marketing/blocks`. That endpoint is UNPAGINATED, so the name search
 * narrows the loaded list in the browser; the kind and archive filters go to the
 * server.
 */

type BlockKind = MarketingBlock['kind'];

const BLOCK_KIND_LABEL: Record<BlockKind, string> = {
  header: 'Header',
  footer: 'Footer',
  section: 'Section',
};

const KIND_OPTIONS: ComboboxOption[] = [
  { value: 'header', label: 'Headers' },
  { value: 'footer', label: 'Footers' },
  { value: 'section', label: 'Sections' },
];

const ARCHIVED_OPTIONS: ComboboxOption[] = [
  { value: 'false', label: 'Active only' },
  { value: 'true', label: 'Archived only' },
];

const SKELETON_ROWS = 4;

function errorText(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return caught instanceof Error ? caught.message : fallback;
}

export function BlockList() {
  const { ability } = useAuth();
  const navigate = useNavigate();
  const canCreate = ability.canCreate('marketing-templates');
  const canUpdate = ability.canUpdate('marketing-templates');
  const canDelete = ability.canDelete('marketing-templates');

  // Several kinds at once (owner 2026-10-28): a comma-separated list, '' = all.
  // A single kind remembered before reads back as a one-item selection.
  const [kind, setKind] = useRememberedState<string>('blocks-kind', '', (raw) =>
    typeof raw === 'string' &&
    raw
      .split(',')
      .every((part) => part === '' || KIND_OPTIONS.some((option) => option.value === part))
      ? raw
      : undefined,
  );
  const kinds = useMemo(() => kind.split(',').filter((part) => part !== ''), [kind]);
  const [archived, setArchived] = useRememberedState<string>('blocks-archived', 'false', (raw) =>
    raw === '' || raw === 'false' || raw === 'true' ? raw : undefined,
  );
  const [search, setSearch] = useRememberedState('blocks-search', '');
  const [reloadKey, setReloadKey] = useState(0);

  const [blocks, setBlocks] = useState<MarketingBlock[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MarketingBlock | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api
      .listMarketingBlocks(
        {
          ...(kinds.length > 0 ? { kind: kinds.join(',') } : {}),
          ...(archived ? { archived: archived === 'true' } : {}),
        },
        controller.signal,
      )
      .then((response) => {
        setBlocks(response.data);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorText(caught, 'Blocks could not be loaded.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [kinds, archived, reloadKey]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = blocks ?? [];
    return term ? list.filter((block) => block.name.toLowerCase().includes(term)) : list;
  }, [blocks, search]);

  async function toggleArchived(block: MarketingBlock) {
    setBusyId(block.id);
    setRowError(null);
    try {
      await api.updateMarketingBlock(block.id, { isArchived: !block.isArchived });
      setReloadKey((k) => k + 1);
    } catch (caught) {
      setRowError(errorText(caught, `${block.name} could not be updated.`));
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteMarketingBlock(pendingDelete.id);
      setPendingDelete(null);
      setReloadKey((k) => k + 1);
    } catch (caught) {
      setDeleteError(errorText(caught, 'Could not reach the server. Nothing was deleted.'));
    } finally {
      setDeleting(false);
    }
  }

  const initialLoad = loading && blocks === null;
  const filtered = kinds.length > 0 || archived !== 'false' || search.trim() !== '';
  const countLabel = initialLoad ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'block' : 'blocks'}`;

  return (
    <>
      <Panel className="min-w-0" aria-labelledby="marketing-blocks-heading">
        <PanelHeader id="marketing-blocks-heading" title="Blocks" description={countLabel} />
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          {canCreate &&
            (['header', 'footer', 'section'] as const).map((k) => (
              <Button
                key={k}
                variant="secondary"
                size="sm"
                leadingIcon={<PlusIcon />}
                onClick={() => navigate(`/marketing/blocks/new?kind=${k}`)}
              >
                New {BLOCK_KIND_LABEL[k].toLowerCase()}
              </Button>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field label="Search blocks" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search blocks…"
                  leadingIcon={<SearchIcon />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-40">
            <Field label="Filter by kind" hideLabel>
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  values={kinds}
                  onChange={(values) => setKind(values.join(','))}
                  options={KIND_OPTIONS}
                  placeholder="All kinds"
                  searchPlaceholder="Search…"
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-48">
            <Field label="Filter by archive state" hideLabel>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={archived}
                  onChange={setArchived}
                  options={ARCHIVED_OPTIONS}
                  emptyOptionLabel="Active and archived"
                  placeholder="Active and archived"
                  searchPlaceholder="Search…"
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {error && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load blocks.</>}>
              <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}
        {rowError && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
              {rowError}
            </Notice>
          </div>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && blocks && 'opacity-60',
          )}
        >
          {initialLoad ? (
            <TableWrap frozenHeader>
              <Table className="min-w-[680px]">
                <BlocksHead />
                <TBody>
                  {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                    <TR key={i}>
                      <TD>
                        <Skeleton className="h-3 w-48" />
                      </TD>
                      <TD>
                        <Skeleton className="h-4 w-16" />
                      </TD>
                      <TD>
                        <Skeleton className="h-3 w-28" />
                      </TD>
                      <TD numeric>
                        <Skeleton className="ml-auto h-6 w-24" />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            error ? (
              <EmptyState title="Nothing to show" description="Couldn’t load the list — see the message above." />
            ) : filtered ? (
              <EmptyState
                title="No blocks match"
                description="Try a different search or kind."
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setKind('');
                      setArchived('false');
                    }}
                  >
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No blocks yet"
                description="Save a header, footer or section once and reuse it across templates."
              />
            )
          ) : (
            <TableWrap frozenHeader>
              <Table className="min-w-[680px]">
                <BlocksHead />
                <TBody>
                  {rows.map((block) => (
                    <TR key={block.id} interactive>
                      <TD>
                        <Link
                          to={`/marketing/blocks/${block.id}`}
                          className="text-sm font-medium text-fg hover:underline [overflow-wrap:anywhere]"
                        >
                          {block.name}
                        </Link>
                        {block.isArchived && (
                          <Badge tone="neutral" className="ml-2">
                            Archived
                          </Badge>
                        )}
                      </TD>
                      <TD>
                        <Badge tone="neutral" variant="outline">{BLOCK_KIND_LABEL[block.kind]}</Badge>
                      </TD>
                      <TD className="text-fg-secondary tabular">{formatDateTime(block.updatedAt)}</TD>
                      <TD numeric>
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            leadingIcon={<PencilIcon />}
                            aria-label={`${canUpdate ? 'Edit' : 'Open'} ${block.name}`}
                            onClick={() => navigate(`/marketing/blocks/${block.id}`)}
                          />
                          {canUpdate && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyId === block.id}
                              onClick={() => void toggleArchived(block)}
                            >
                              {block.isArchived ? 'Unarchive' : 'Archive'}
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="danger"
                              size="sm"
                              iconOnly
                              leadingIcon={<TrashIcon />}
                              aria-label={`Delete ${block.name}`}
                              onClick={() => {
                                setDeleteError(null);
                                setPendingDelete(block);
                              }}
                            />
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </div>
      </Panel>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        tone="danger"
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete block?'}
        description={
          pendingDelete?.kind === 'section'
            ? 'The block will be deleted and can’t be recovered.'
            : 'The block will be deleted and can’t be recovered. Templates using it as their header or footer will have none.'
        }
        confirmLabel="Delete block"
        loading={deleting}
        error={deleteError && <span className="[overflow-wrap:anywhere]">{deleteError}</span>}
      />
    </>
  );
}

function BlocksHead() {
  return (
    <THead>
      <TR>
        <TH>Name</TH>
        <TH className="w-28">Kind</TH>
        <TH className="w-44">Updated</TH>
        <TH className="w-52" numeric>
          Actions
        </TH>
      </TR>
    </THead>
  );
}
